import React from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, Avatar, Button } from '@heroui/react';
import { X, Mail, Building2, Briefcase, FileText } from 'lucide-react';
import { useUser } from '@/hooks/queries/useUsers';
import { useTranslation } from '@/hooks/useTranslation';
import { Skeleton } from '@heroui/react';

interface UserDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
}

const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({ isOpen, onClose, userId }) => {
  const { t } = useTranslation('people');
  const {
    data: user,
    isLoading,
    error,
  } = useUser({
    id: userId || '',
    shouldSkip: !isOpen || !userId,
  });

  const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User' : '';

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      closeButton={
        <Button
          isIconOnly
          variant="light"
          aria-label="Close drawer"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="w-5 h-5" />
        </Button>
      }
      placement="right"
      size="md"
    >
      <DrawerContent>
        <DrawerHeader className="flex flex-col gap-4 border-b border-gray-200 dark:border-gray-800 pb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('drawer.title')}</h2>
        </DrawerHeader>
        <DrawerBody className="p-6">
          {isLoading && (
            <div className="flex flex-col items-center gap-6">
              <Skeleton className="w-32 h-32 rounded-full" />
              <div className="w-full space-y-3">
                <Skeleton className="h-6 w-48 rounded-lg" />
                <Skeleton className="h-4 w-64 rounded-lg" />
                <Skeleton className="h-4 w-56 rounded-lg" />
              </div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <X className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('drawer.error.title')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('drawer.error.message')}</p>
            </div>
          )}

          {user && !isLoading && !error && (
            <div className="flex flex-col gap-6">
              {/* Avatar and Name */}
              <div className="flex flex-col items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-800">
                <Avatar
                  src={user.avatarFile?.url}
                  name={fullName}
                  size="lg"
                  className="w-32 h-32 text-2xl font-semibold"
                  classNames={{
                    base: 'ring-4 ring-primary/20',
                  }}
                />
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{fullName}</h3>
                  {user.position && <p className="text-lg text-gray-600 dark:text-gray-400">{user.position}</p>}
                </div>
              </div>

              {/* User Details */}
              <div className="space-y-4">
                {/* Email */}
                {user.email && (
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                      <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{t('drawer.email')}</p>
                      <p className="text-base text-gray-900 dark:text-white break-all">{user.email}</p>
                    </div>
                  </div>
                )}

                {/* Department */}
                {user.department && (
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                      <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('drawer.department')}
                      </p>
                      <p className="text-base text-gray-900 dark:text-white">{user.department.name}</p>
                    </div>
                  </div>
                )}

                {/* Position */}
                {user.position && (
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                      <Briefcase className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('drawer.position')}
                      </p>
                      <p className="text-base text-gray-900 dark:text-white">{user.position}</p>
                    </div>
                  </div>
                )}

                {/* Position Description */}
                {user.positionDescription && (
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                      <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('drawer.positionDescription')}
                      </p>
                      <p className="text-base text-gray-900 dark:text-white whitespace-pre-wrap">
                        {user.positionDescription}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default UserDetailsDrawer;
